import { inject, Injectable } from '@angular/core';
import {
  ProductsCreatePayload,
  ProductsDetailsResponse,
  ProductsListDetailsResponse,
  ProductsTableKeys,
  ProductsUpdatePayload,
} from '@ag-common-lib/public-api';
import { LoadOptions } from 'devextreme/common/data';
import { ReportingApiService } from './reporting-api.service';
import { OverrideOption, OverrideOptionsResponse } from './override-options.service';

const PRODUCTS_GRID_DEFAULT_SORT = [{ selector: ProductsTableKeys.productName, desc: false }];

@Injectable({ providedIn: 'root' })
export class ProductApiService {
  private readonly basePath = '/api/products';

  private readonly reportingApiService = inject(ReportingApiService);

  getProductsList(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined = {},
  ): Promise<ProductsListDetailsResponse> {
    console.log('getProductsList loadOptions', loadOptions);
    return this.reportingApiService.request<ProductsListDetailsResponse>(`${this.basePath}/get-list`, {
      method: 'POST',
      body: this.normalizeLoadOptions(loadOptions),
    });
  }

  getProductsListAll(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined = {},
  ): Promise<ProductsListDetailsResponse> {
    return this.reportingApiService.request<ProductsListDetailsResponse>(`${this.basePath}/get-list/all`, {
      method: 'POST',
      body: this.normalizeLoadOptions(loadOptions),
    });
  }

  async getProductById(id: number): Promise<ProductsDetailsResponse['data']> {
    const response = await this.reportingApiService.request<ProductsDetailsResponse>(`${this.basePath}/${id}`);
    return response.data;
  }

  async createProduct(payload: ProductsCreatePayload): Promise<ProductsDetailsResponse['data']> {
    const response = await this.reportingApiService.request<ProductsDetailsResponse>(this.basePath, {
      method: 'POST',
      body: payload,
    });
    return response.data;
  }

  async updateProduct(id: number, payload: ProductsUpdatePayload): Promise<ProductsDetailsResponse['data']> {
    const response = await this.reportingApiService.request<ProductsDetailsResponse>(`${this.basePath}/${id}`, {
      method: 'PUT',
      body: payload,
    });
    return response.data;
  }

  async deleteProduct(id: number): Promise<void> {
    await this.reportingApiService.request<void>(`${this.basePath}/${id}`, {
      method: 'DELETE',
    });
  }

  getDistinctCarrierNames(): Promise<string[]> {
    return this.reportingApiService
      .request<OverrideOptionsResponse>('/api/override/options/carriers/get-list', {
        method: 'POST',
        body: {
          skip: 0,
          requireTotalCount: false,
          sort: [],
          filter: [],
        },
      })
      .then(response => this.mapDistinctCarrierNames(response.options ?? response.data));
  }

  private mapDistinctCarrierNames(rows: OverrideOption[] | undefined): string[] {
    const carrierNames = new Set<string>();

    (rows ?? []).forEach(row => {
      const carrierName = String(row?.value ?? '').trim();

      if (!carrierName) {
        return;
      }

      carrierNames.add(carrierName);
    });

    return Array.from(carrierNames).sort((left, right) => left.localeCompare(right));
  }

  private normalizeLoadOptions(
    loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    if (!loadOptions || typeof loadOptions !== 'object') {
      return {
        requireTotalCount: true,
        sort: PRODUCTS_GRID_DEFAULT_SORT,
      };
    }

    const normalized: Record<string, unknown> = {
      ...(loadOptions as Record<string, unknown>),
      requireTotalCount: true,
    };

    if (!normalized['sort']) {
      normalized['sort'] = PRODUCTS_GRID_DEFAULT_SORT;
    }

    return normalized;
  }
}
